import { useState } from "react";
import { Text, View } from "react-native";
import { useTranslation } from "react-i18next";
import {
  formatWeight,
  fromCanonicalKg,
  toCanonicalKg,
  type UnitsPreference,
  parseLocaleNumber,
} from "@heraion/shared";

import { Button } from "../primitives/Button";
import { TextField } from "../primitives/TextField";
import { makeQuickEntryCardStyles } from "./glanceCardStyles";
import { weightUnitLabels } from "../weightUnitLabels";
import type { UiTheme } from "../theme";

// Home quick weigh-in. The most-changed body value reachable in ~one tap
// (home-redesign.md: Home owns the quick-add; Settings -> Measurements does
// NOT duplicate it). Lifted into @heraion/ui so mobile + the web-rnw
// console render one card. The write itself is a host seam (onSave): mobile
// goes through useMeasurements (outbox + optimistic overlay) then refreshes
// the profile; web POSTs /api/me/measurements. A queued write says nothing
// here («work by default», 12/08; #295).
const numOrUndef = (s: string): number | undefined => {
  const v = parseLocaleNumber(s);
  return Number.isFinite(v) && v > 0 ? v : undefined;
};

export function QuickWeightCard({
  theme,
  units,
  bodyweightKg,
  onSave,
  value: controlledValue,
  onChangeText: controlledOnChangeText,
}: {
  theme: UiTheme;
  units: UnitsPreference;
  /** Canonical (kg) current bodyweight, or null when unknown. */
  bodyweightKg: number | null;
  /** Persist a canonical-kg bodyweight; returns true on success. */
  onSave: (canonicalKg: number) => Promise<boolean>;
  /** Optional controlled draft (MX-8b: Home lifts it above the wide/narrow
   *  split point so a resize mid-type survives; keyed by widget id, never
   *  position, per HR-11's future organisable Home). Omit to keep this
   *  card's own uncontrolled draft, unchanged (MetabolicCard,
   *  MeasurementsScreen, SettingsMeasurements). */
  value?: string;
  onChangeText?: (v: string) => void;
}) {
  const { t, i18n } = useTranslation();
  const styles = makeQuickEntryCardStyles(theme);

  // Narrowed once: `knownKg` is the number, `current` its display-unit twin,
  // so the formatter below needs no cast.
  const knownKg = typeof bodyweightKg === "number" ? bodyweightKg : null;
  const current = knownKg !== null ? fromCanonicalKg(knownKg, units) : null;

  const [draft, setDraft] = useState("");
  const value = controlledValue ?? draft;
  const setValue = controlledOnChangeText ?? setDraft;
  const [saving, setSaving] = useState(false);
  const [justSaved, setJustSaved] = useState(false);

  const submit = async () => {
    const parsed = numOrUndef(value);
    if (parsed === undefined || saving) return;
    setSaving(true);
    setJustSaved(false);
    try {
      const ok = await onSave(toCanonicalKg(parsed, units));
      if (ok) {
        setValue("");
        setJustSaved(true);
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <View style={styles.card}>
      <View style={styles.textCol}>
        <Text style={styles.label}>
          {t("mobile.home.weightTitle", { defaultValue: "Body weight" })}
        </Text>
        <Text
          style={[
            styles.hint,
            current !== null && !justSaved && styles.hintValue,
          ]}
        >
          {justSaved
            ? t("mobile.home.weightSaved", { defaultValue: "Saved ✓" })
            : knownKg !== null
              ? t("mobile.home.weightCurrent", {
                  defaultValue: "Latest: {{weight}}",
                  weight: formatWeight(
                    knownKg,
                    units,
                    i18n.language,
                    weightUnitLabels(t),
                  ),
                })
              : t("mobile.home.weightNone", {
                  defaultValue: "Log your weight to track trends.",
                })}
        </Text>
      </View>
      <TextField
        theme={theme}
        numeric
        style={styles.input}
        value={value}
        onChangeText={setValue}
        placeholder={current !== null ? String(current) : units}
        accessibilityLabel={t("mobile.home.weightTitle", {
          defaultValue: "Body weight",
        })}
        returnKeyType="done"
        onSubmitEditing={() => void submit()}
      />
      <Button
        theme={theme}
        label={t("mobile.home.weightSave", { defaultValue: "Save" })}
        // Two "Save" buttons sit on this screen — this one and the glucose
        // card's — and a screen reader announced both as bare "Save", with
        // only position to tell them apart (DS-47, seen in the QA bench's
        // accessibility map). The visible label stays short; the announced one
        // says what it saves.
        accessibilityLabel={t("mobile.home.weightSaveA11y", {
          defaultValue: "Save body weight",
        })}
        onPress={() => void submit()}
        loading={saving}
        disabled={numOrUndef(value) === undefined}
        style={styles.saveBtn}
      />
    </View>
  );
}

